/**
 * Sistema de notificaciones nativas con Service Worker
 * Maneja push notifications, notificaciones de órdenes, y eventos en tiempo real
 */

export interface NativeNotification {
  id?: string;
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  data?: Record<string, any>;
  requireInteraction?: boolean;
  actions?: Array<{
    action: string;
    title: string;
    icon?: string;
  }>;
}

export interface OrderNotificationEvent {
  order_id: string;
  order_number: string;
  event_type: 'status_change' | 'courier_assigned' | 'courier_arriving' | 'payment_received' | 'ready_for_pickup' | 'delivered' | 'cancelled';
  title: string;
  body: string;
  delivery_address?: string;
  courier_name?: string;
  estimated_time?: number; // minutos
}

/**
 * Registrar Service Worker
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.warn('[Notifications] Service Worker no soportado');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
      updateViaCache: 'none',
    });
    return registration;
  } catch (error) {
    console.error('[Notifications] Error registrando Service Worker:', error);
    return null;
  }
}

/**
 * Solicitar permiso para notificaciones
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    console.warn('[Notifications] Notifications no soportadas');
    return 'denied';
  }

  if (Notification.permission !== 'default') {
    return Notification.permission;
  }

  try {
    const permission = await Notification.requestPermission();
    localStorage.setItem('notification-permission', permission);
    return permission;
  } catch (error) {
    console.error('[Notifications] Error solicitando permiso:', error);
    return 'denied';
  }
}

/**
 * Mostrar notificación nativa
 */
export async function showNativeNotification(notification: NativeNotification): Promise<void> {
  // Verificar que tenemos permiso
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    console.warn('[Notifications] Permissions no otorgados');
    return;
  }

  // Si hay Service Worker, usarlo
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'SHOW_NOTIFICATION',
      data: notification,
    });
    return;
  }

  // Fallback: crear notificación directamente
  try {
    new Notification(notification.title, {
      body: notification.body,
      icon: notification.icon || '/minibid-icon.png',
      badge: notification.badge || '/minibid-badge.png',
      tag: notification.tag || 'minibid',
      requireInteraction: notification.requireInteraction || false,
      data: notification.data,
    });
  } catch (error) {
    console.error('[Notifications] Error mostrando notificación:', error);
  }
}

/**
 * Notificación de cambio de estado de orden
 */
export async function notifyOrderStatusChange(event: OrderNotificationEvent): Promise<void> {
  const emojis: Record<string, string> = {
    status_change: '📝',
    courier_assigned: '🚴',
    courier_arriving: '📍',
    payment_received: '💳',
    ready_for_pickup: '📦',
    delivered: '✅',
    cancelled: '❌',
  };

  const emoji = emojis[event.event_type] || '📢';

  await showNativeNotification({
    title: `${emoji} ${event.title}`,
    body: event.body,
    tag: `order-${event.order_id}`,
    requireInteraction: event.event_type === 'courier_arriving' || event.event_type === 'cancelled',
    data: {
      order_id: event.order_id,
      order_number: event.order_number,
      event_type: event.event_type,
      delivery_address: event.delivery_address,
      courier_name: event.courier_name,
      estimated_time: event.estimated_time,
    },
  });

  // También loguear en analytics
}

/**
 * Sonido para notificaciones
 */
export function playNotificationSound(type: 'chime' | 'alert' | 'success' = 'chime'): void {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

    const frequencies: Record<string, number[]> = {
      chime: [523.25, 659.25, 783.99], // Do, Mi, Sol
      alert: [800, 800, 800], // Tono de alerta
      success: [523.25, 659.25, 783.99, 1046.5], // Escala ascendente
    };

    const freq = frequencies[type] || frequencies.chime;
    let time = audioContext.currentTime;

    freq.forEach((f, i) => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();

      osc.connect(gain);
      gain.connect(audioContext.destination);

      osc.frequency.value = f;
      osc.type = 'sine';

      gain.gain.setValueAtTime(0.3, time);
      gain.gain.exponentialRampToValueAtTime(0.01, time + 0.1);

      osc.start(time);
      osc.stop(time + 0.1);

      time += 0.15;
    });
  } catch (error) {
    console.warn('[Notifications] Audio context no disponible:', error);
  }
}

/**
 * Vibración para notificaciones
 */
export function vibrateForNotification(type: 'gentle' | 'normal' | 'strong' = 'normal'): void {
  if (!navigator.vibrate) {
    return;
  }

  const patterns: Record<string, number | number[]> = {
    gentle: [50],
    normal: [100, 50, 100],
    strong: [200, 100, 200, 100, 200],
  };

  navigator.vibrate(patterns[type] || patterns.normal);
}

/**
 * Badge (contador en el ícono de la app)
 * Solo funciona en navegadores modernos
 */
export async function setNotificationBadge(count: number): Promise<void> {
  if ('setAppBadge' in navigator) {
    try {
      if (count === 0) {
        await (navigator as any).clearAppBadge();
      } else {
        await (navigator as any).setAppBadge(count);
      }
    } catch (error) {
      console.warn('[Notifications] Badge no disponible:', error);
    }
  }
}

/**
 * Verificar si las notificaciones están habilitadas
 */
export function areNotificationsEnabled(): boolean {
  return 'Notification' in window && Notification.permission === 'granted';
}

/**
 * Obtener estado del permiso
 */
export function getNotificationPermissionStatus(): NotificationPermission {
  if (!('Notification' in window)) {
    return 'denied';
  }
  return Notification.permission;
}
